import React, { useState, useEffect, useRef } from "react";
import { generateThumbnailFromUrl } from "../utils";
import {
  Folder as FolderIcon,
  FolderOpen,
  Search,
  Plus,
  ShoppingBag,
  Menu,
  MoreVertical,
  Edit2,
  Trash2,
  FileVideo,
  Share2,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  FolderPlus,
  Copy,
  RefreshCw
} from 'lucide-react';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';

/* --- Types --- */
export interface Asset {
  id: string;
  name: string;
  type: 'video' | 'audio' | 'image' | 'other';
  url: string;
  size: number;
}

export interface Project {
  id: string;
  title: string;
  assets: Asset[];
  updated: string;
  thumbnailUrl?: string;
}

export interface FolderPreset {
  key: string;
  label: string;
  icon: string; // Keeps material icon string for presets if needed, or we map it
  color: string;
}

export interface Folder {
  id: string;
  name: string;
  open: boolean;
  projects: Project[];
  preset?: FolderPreset;
}

/* --- Utils --- */
const uid = () => "id-" + Math.random().toString(36).slice(2, 8) + Date.now().toString(36);

const fileType = (t: string): Asset['type'] =>
  t.startsWith("video") ? "video" : t.startsWith("audio") ? "audio" : t.startsWith("image") ? "image" : "other";

const makeProject = (title: string, assets: Asset[] = []): Project => ({
  id: uid(),
  title,
  assets,
  updated: "just now",
});

/* =========================
   Sidebar: Folders + Projects
   ========================= */
interface SidebarProjectListProps {
  sidebarOpen: boolean;
  onToggle: () => void;
  onReset?: () => void;
  folders: Folder[];
  setFolders: React.Dispatch<React.SetStateAction<Folder[]>>;
  rootProjects: Project[];
  setRootProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  onSelectProject: (project: Project) => void;
  onDeleteProject?: (projectId: string) => void;
  onRenameProject?: (project: Project, newName: string) => void;
  onDuplicateProject?: (project: Project) => void;
  onUpdateProject?: (project: Project) => void;
  onShareProject?: (project: Project) => void;
  onOpenStore?: () => void;
  onOpenLibrary?: () => void;
  lockOpen?: boolean;
  autoCloseOnSelect?: boolean;
  autoCloseOnOutside?: boolean;
  autoCloseOnEscape?: boolean;
  onUploadAsset?: (file: File, assetId: string, projectId: string) => void;
  currentProjectId?: string | null;
}

export default function SidebarProjectList({
  sidebarOpen,
  onToggle,
  folders,
  setFolders,
  rootProjects,
  setRootProjects,
  onSelectProject,
  onDuplicateProject,
  onDeleteProject,
  onRenameProject,
  onShareProject,
  onUpdateProject,
  onOpenStore,
  lockOpen = false,
  autoCloseOnSelect = false,
  autoCloseOnOutside = false,
  autoCloseOnEscape = false,
  currentProjectId,
  onUploadAsset,
}: SidebarProjectListProps) {
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Delete Confirmation State
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    type: 'project' | 'folder';
    id: string;
    name: string;
    object?: any;
  }>({ open: false, type: 'project', id: '', name: '' });

  // Rename State
  const [renameState, setRenameState] = useState<{
    open: boolean;
    type: 'project' | 'folder';
    id: string;
    name: string;
    object?: any;
  }>({ open: false, type: 'project', id: '', name: '' });
  const [newNameInput, setNewNameInput] = useState("");

  const collapsed = !sidebarOpen;
  const asideRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* -------- Helpers -------- */
  const ensureOpen = () => {
    if (!sidebarOpen && typeof onToggle === "function") onToggle();
  };
  const ensureClose = () => {
    if (sidebarOpen && !lockOpen && typeof onToggle === "function") onToggle();
  };

  /* -------- Auto-close implementations -------- */
  useEffect(() => {
    if (!sidebarOpen || !autoCloseOnOutside) return;
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      if (lockOpen || showFolderModal || deleteConfirmation.open) return;
      const el = asideRef.current;
      if (!el) return;

      const path = (e as any).composedPath ? (e as any).composedPath() : [];
      // Simple check if click is outside sidebar and not in a modal
      const clickedInside = path.includes(el);
      if (!clickedInside) {
        // Rudimentary check to see if we clicked on a modal
        const isModal = path.some((node: HTMLElement) =>
          node.classList?.contains('fixed') && node.innerHTML?.includes('back') // Very loose check
        );
      }
    };
  }, [sidebarOpen, autoCloseOnOutside]);

  // Project Management Logic (kept mostly same)
  const toggleFolderOpen = (id: string) =>
    setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, open: !f.open } : f)));

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    setFolders((prev) => [{ id: uid(), name: newFolderName.trim(), open: true, projects: [] }, ...prev]);
    setShowFolderModal(false);
    setNewFolderName("");
  };

  const createProjectRoot = () => {
    const newP = makeProject("Nouveau projet", []);
    setRootProjects((prev) => [newP, ...prev]);
    onSelectProject(newP);
    ensureOpen();
  };

  const moveProjectToFolder = (projectId: string, fromFolderId: string | null, toFolderId: string | null) => {
    if (fromFolderId === toFolderId) return;
    let project: Project | undefined;
    if (fromFolderId) {
      const folder = folders.find(f => f.id === fromFolderId);
      project = folder?.projects.find(p => p.id === projectId);
    } else {
      project = rootProjects.find(p => p.id === projectId);
    }
    if (!project) return;

    if (fromFolderId) {
      setFolders(prev => prev.map(f => f.id === fromFolderId ? { ...f, projects: f.projects.filter(p => p.id !== projectId) } : f));
    } else {
      setRootProjects(prev => prev.filter(p => p.id !== projectId));
    }

    if (toFolderId) {
      setFolders(prev => prev.map(f => f.id === toFolderId ? { ...f, projects: [project!, ...f.projects], open: true } : f));
    } else {
      setRootProjects(prev => [project!, ...prev]);
    }
  };

  const onProjectDragStart = (e: React.DragEvent, p: Project, fromFolderId?: string) => {
    e.stopPropagation();
    e.dataTransfer.setData("project-id", p.id);
    if (fromFolderId) e.dataTransfer.setData("from-folder-id", fromFolderId);
  };

  const handleSelectProject = (p: Project) => {
    onSelectProject(p);
    if (autoCloseOnSelect) ensureClose();
  };

  const onRootDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverRoot(false);
    const files = e.dataTransfer.files;
    const movedProjectId = e.dataTransfer.getData("project-id");

    if (movedProjectId) {
      const fromFolderId = e.dataTransfer.getData("from-folder-id");
      if (fromFolderId) moveProjectToFolder(movedProjectId, fromFolderId, null);
      return;
    }

    if (!files?.length) return;
    const fileArray = Array.from(files);
    const assets = fileArray.map((file: File) => ({
      id: uid(),
      name: file.name,
      type: fileType(file.type),
      url: URL.createObjectURL(file),
      size: file.size,
      originalFile: file
    })).filter((a) => a.type !== "other");

    if (!assets.length) return;

    let thumbnail = "";
    const firstVideo = assets.find(a => a.type === "video");
    if (firstVideo) {
      try {
        thumbnail = await generateThumbnailFromUrl(firstVideo.url);
      } catch (err) { console.error(err); }
    }

    const finalAssets = assets.map(({ originalFile, ...rest }) => rest);
    const newP = makeProject("", finalAssets);
    newP.thumbnailUrl = thumbnail;

    setRootProjects((prev) => [newP, ...prev]);
    onSelectProject(newP);
    if (autoCloseOnSelect) ensureClose();

    if (onUploadAsset) {
      assets.forEach(asset => onUploadAsset(asset.originalFile as File, asset.id, newP.id));
    }
  };

  const onFolderDrop = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
    const movedProjectId = e.dataTransfer.getData("project-id");

    if (movedProjectId) {
      const fromFolderId = e.dataTransfer.getData("from-folder-id") || null;
      if (fromFolderId === targetFolderId) return;
      moveProjectToFolder(movedProjectId, fromFolderId, targetFolderId);
    }
  };

  const handleDragOverFolder = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(id);
  };

  // Styles
  const bgClass = sidebarOpen ? "bg-surface-950 border-r border-surface-700" : "bg-black border-r border-surface-800";
  const textClass = "text-text-secondary";

  /* --- Components --- */
  const MiniButton = ({ icon: Icon, label, onClick }: { icon: any, label: string, onClick: () => void }) => (
    <button onClick={onClick} className="w-10 h-10 flex items-center justify-center hover:bg-white/10 text-text-secondary hover:text-lvmh-gold rounded-none transition-colors" title={label}>
      <Icon size={20} />
    </button>
  );

  return (
    <>
      <aside
        ref={asideRef}
        className={`fixed left-0 top-0 h-full z-50 flex flex-col transition-all duration-300 ease-out ${sidebarOpen ? 'w-80 bg-white border-r border-gray-200' : 'w-16 bg-black border-r border-gray-800'} shadow-xl`}
      >
        {/* Header */}
        {collapsed ? (
          <div className="h-full py-4 flex flex-col items-center gap-2">
            <button onClick={onToggle} className="w-10 h-10 flex items-center justify-center hover:bg-white/10 text-gray-500 hover:text-white rounded-full transition-all" title="Expand">
              <PanelLeftOpen size={20} />
            </button>
            <div className="w-6 h-px bg-gray-800 my-2" />
            <button onClick={createProjectRoot} className="w-10 h-10 flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-blue-400 rounded-full transition-all" title="New Project">
              <Plus size={20} />
            </button>
            <button onClick={() => { setShowSearch(true); onToggle(); }} className="w-10 h-10 flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-white rounded-full transition-all" title="Search">
              <Search size={20} />
            </button>
            <button onClick={() => onOpenStore && onOpenStore()} className="w-10 h-10 flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-amber-400 rounded-full transition-all" title="Store">
              <ShoppingBag size={18} />
            </button>
          </div>
        ) : (
          <div className="h-16 px-4 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
            <span className="text-lg font-bold tracking-wide text-gray-800">Projects</span>
            <button
              onClick={lockOpen ? undefined : onToggle}
              className={`p-2 rounded-full transition-all ${lockOpen ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
            >
              <PanelLeftClose size={18} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 overflow-y-auto ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Quick Actions */}
          <div className="p-4 space-y-3">
            <button onClick={createProjectRoot} className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-full transition-all active:scale-95 shadow-lg shadow-blue-200">
              <Plus size={16} /> New Project
            </button>

            <div className="flex gap-2">
              {showSearch ? (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
                  <input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                    placeholder="Search projects..."
                    className="w-full bg-gray-50 border border-gray-200 text-sm text-gray-700 placeholder:text-gray-400 py-2 pl-9 pr-8 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                  />
                  <button
                    onClick={() => { setSearchQuery(''); setShowSearch(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowSearch(true)} className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 text-sm rounded-full transition-all">
                  <Search size={14} /> Search
                </button>
              )}
              <button onClick={onOpenStore} className="p-2 rounded-full bg-gray-100 hover:bg-amber-100 text-gray-500 hover:text-amber-600 transition-all" title="Token Store">
                <ShoppingBag size={16} />
              </button>
            </div>

            {/* New Folder Button */}
            <button onClick={() => setShowFolderModal(true)} className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 text-sm font-medium rounded-full transition-all">
              <FolderPlus size={16} /> New Folder
            </button>
          </div>

          {/* Folders */}
          <div className="px-4 pb-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Folders</span>
            </div>

            {/* Root Dropzone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOverRoot(true); }}
              onDragLeave={() => setDragOverRoot(false)}
              onDrop={onRootDrop}
              className={`transition-all duration-200 border border-dashed rounded-lg ${dragOverRoot ? 'h-12 border-blue-500 bg-blue-500/10' : 'h-0 border-transparent'} mb-2 flex items-center justify-center`}
            >
              {dragOverRoot && <span className="text-blue-400 text-xs font-medium">Drop here</span>}
            </div>

            <div className="space-y-0.5">
              {folders.map(folder => {
                const matchesSearch = (p: Project) => !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase());
                const filteredProjects = folder.projects.filter(matchesSearch);

                if (searchQuery && filteredProjects.length === 0 && !folder.name.toLowerCase().includes(searchQuery.toLowerCase())) return null;

                return (
                  <div
                    key={folder.id}
                    className={`group/folder mb-1 rounded-lg transition-all ${dragOverFolderId === folder.id ? 'bg-blue-50 ring-2 ring-blue-500/50' : ''}`}
                    onDragOver={(e) => handleDragOverFolder(e, folder.id)}
                    onDragLeave={() => setDragOverFolderId(null)}
                    onDrop={(e) => onFolderDrop(e, folder.id)}
                  >
                    <div
                      onClick={() => toggleFolderOpen(folder.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all group relative cursor-pointer"
                    >
                      {folder.open ? <FolderOpen size={16} className="text-blue-500" /> : <FolderIcon size={16} className="text-gray-400 group-hover:text-blue-500" />}
                      <span className="truncate flex-1 text-left font-medium">{folder.name}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{filteredProjects.length}</span>

                      <div className="absolute right-1 opacity-0 group-hover:opacity-100 flex gap-1 bg-white shadow-lg rounded-md px-1 py-0.5 border border-gray-200">
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setRenameState({ open: true, type: 'folder', id: folder.id, name: folder.name, object: folder });
                          setNewNameInput(folder.name);
                        }} className="p-1 hover:text-blue-500 text-gray-400 rounded"><Edit2 size={12} /></button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmation({ open: true, type: 'folder', id: folder.id, name: folder.name, object: folder });
                        }} className="p-1 hover:text-red-500 text-gray-400 rounded"><Trash2 size={12} /></button>
                      </div>
                    </div>

                    {(folder.open || searchQuery) && (
                      <div className="ml-5 border-l border-gray-200 pl-3 mt-1 space-y-1">
                        {filteredProjects.map(p => (
                          <ProjectItem
                            key={p.id}
                            project={p}
                            isActive={currentProjectId === p.id}
                            onClick={() => handleSelectProject(p)}
                            onDragStart={(e) => onProjectDragStart(e, p, folder.id)}
                            actions={{
                              rename: (n) => {
                                setRenameState({ open: true, type: 'project', id: p.id, name: p.title, object: p });
                                setNewNameInput(p.title);
                              },
                              delete: () => setDeleteConfirmation({ open: true, type: 'project', id: p.id, name: p.title }),
                              share: () => onShareProject?.(p),
                              duplicate: () => onDuplicateProject?.(p),
                              update: () => onUpdateProject?.(p)
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Root Projects */}
          <div className="px-4 mt-4">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Projects</div>
            <div className="space-y-1">
              {rootProjects.filter(p => !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase())).map(p => (
                <ProjectItem
                  key={p.id}
                  project={p}
                  isActive={currentProjectId === p.id}
                  onClick={() => handleSelectProject(p)}
                  onDragStart={(e) => onProjectDragStart(e, p)}
                  actions={{
                    rename: (n) => {
                      setRenameState({ open: true, type: 'project', id: p.id, name: p.title, object: p });
                      setNewNameInput(p.title);
                    },
                    delete: () => setDeleteConfirmation({ open: true, type: 'project', id: p.id, name: p.title }),
                    share: () => onShareProject?.(p),
                    duplicate: () => onDuplicateProject?.(p),
                    update: () => onUpdateProject?.(p)
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* Modals */}
      <Modal
        isOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        title="Nouveau Dossier"
        size="sm"
        variant="light"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowFolderModal(false)}>Annuler</Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Créer</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nom du dossier</label>
            <input
              ref={inputRef}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Ex: Campagne 2024"
              className="w-full bg-white border border-gray-300 p-2 text-gray-900 focus:border-lvmh-gold outline-none rounded-md"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <p className="text-xs text-text-muted flex items-start gap-2">
            <span className="text-lvmh-gold">💡</span>
            Les dossiers permettent d'organiser vos projets par type, client ou saison.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation(prev => ({ ...prev, open: false }))}
        title="Confirmation de suppression"
        size="sm"
        variant="light"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirmation(prev => ({ ...prev, open: false }))}>Annuler</Button>
            <Button variant="danger" onClick={() => {
              const { type, id, object } = deleteConfirmation;
              if (type === 'project') {
                onDeleteProject?.(id);
              } else {
                setFolders(prev => prev.filter(f => f.id !== id));
                if (object?.projects?.length) {
                  setRootProjects(prev => [...object.projects, ...prev]);
                }
              }
              setDeleteConfirmation(prev => ({ ...prev, open: false }));
            }}>Supprimer</Button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="text-red-500" size={24} />
          </div>
          <p className="text-gray-900 font-medium mb-1">
            Supprimer {deleteConfirmation.type === 'folder' ? 'la dossier' : 'le projet'} "{deleteConfirmation.name}" ?
          </p>
          <p className="text-gray-500 text-sm">
            {deleteConfirmation.type === 'folder'
              ? "Les projets à l'intérieur seront déplacés à la racine."
              : "Cette action est irréversible et effacera toutes les données."}
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={renameState.open}
        onClose={() => setRenameState(prev => ({ ...prev, open: false }))}
        title={`Renommer ${renameState.type === 'folder' ? 'le dossier' : 'le projet'}`}
        size="sm"
        variant="light"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameState(prev => ({ ...prev, open: false }))}>Annuler</Button>
            <Button onClick={() => {
              const { type, id, object } = renameState;
              if (!newNameInput.trim() || newNameInput === renameState.name) {
                setRenameState(prev => ({ ...prev, open: false }));
                return;
              }

              if (type === 'folder') {
                setFolders(prev => prev.map(f => f.id === id ? { ...f, name: newNameInput } : f));
              } else {
                onRenameProject?.(object, newNameInput);
              }
              setRenameState(prev => ({ ...prev, open: false }));
            }}>Sauvegarder</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nouveau nom</label>
            <input
              autoFocus
              value={newNameInput}
              onChange={(e) => setNewNameInput(e.target.value)}
              className="w-full bg-white border border-gray-300 p-2 text-gray-900 focus:border-lvmh-gold outline-none rounded-md"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const { type, id, object } = renameState;
                  if (!newNameInput.trim() || newNameInput === renameState.name) {
                    setRenameState(prev => ({ ...prev, open: false }));
                    return;
                  }
                  if (type === 'folder') {
                    setFolders(prev => prev.map(f => f.id === id ? { ...f, name: newNameInput } : f));
                  } else {
                    onRenameProject?.(object, newNameInput);
                  }
                  setRenameState(prev => ({ ...prev, open: false }));
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

// Sub-component for Project Item to keep list clean
const ProjectItem = ({
  project,
  isActive,
  onClick,
  onDragStart,
  actions
}: {
  key?: string,
  project: Project,
  isActive: boolean,
  onClick: () => void,
  onDragStart: (e: React.DragEvent) => void,
  actions: { rename: (n: string) => void, delete: () => any, share: () => void, duplicate: () => void, update?: () => void }
}) => {
  return (
    <div className="group relative">
      <button
        onClick={onClick}
        draggable
        onDragStart={onDragStart}
        className={`w-full flex items-center gap-3 p-2.5 text-left transition-all rounded-lg border
          ${isActive
            ? 'bg-blue-50 border-blue-200 text-gray-800 shadow-sm'
            : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-800'
          }`}
      >
        {project.thumbnailUrl ? (
          <img src={project.thumbnailUrl} className="w-16 h-9 object-cover rounded-md bg-gray-100 ring-1 ring-gray-900/5 shadow-sm" />
        ) : (
          <div className="w-16 h-9 flex items-center justify-center bg-gray-100 text-gray-400 rounded-md ring-1 ring-gray-900/5 shadow-sm">
            <FileVideo size={18} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{project.title || "Untitled"}</div>
          <div className="text-[10px] text-gray-400 truncate">{project.updated}</div>
        </div>
      </button>

      {/* Hover Actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 flex items-center gap-1 bg-white shadow-lg rounded-full px-1.5 py-1 border border-gray-200 transition-opacity">
        {actions.update && (
          <button onClick={(e) => { e.stopPropagation(); actions.update!(); }} className="p-1.5 hover:bg-orange-50 hover:text-orange-600 text-gray-400 rounded-full transition-colors" title="Mettre à jour (Legacy)">
            <RefreshCw size={12} />
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); actions.share(); }} className="p-1.5 hover:bg-green-50 hover:text-green-600 text-gray-400 rounded-full transition-colors" title="Share">
          <Share2 size={12} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); actions.duplicate(); }} className="p-1.5 hover:bg-purple-50 hover:text-purple-600 text-gray-400 rounded-full transition-colors" title="Duplicate">
          <Copy size={12} />
        </button>
        <button onClick={(e) => {
          e.stopPropagation();
          actions.rename(project.title);
        }} className="p-1.5 hover:bg-blue-50 hover:text-blue-600 text-gray-400 rounded-full transition-colors" title="Rename">
          <Edit2 size={12} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); actions.delete(); }} className="p-1.5 hover:bg-red-50 hover:text-red-600 text-gray-400 rounded-full transition-colors" title="Delete">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
};
